import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { quoteService } from '../services/quoteService';
import { quotePDFService } from '../services/quotePDFService';
import { quoteEmailService } from '../services/quoteEmailService';
import { getMonthlyPaymentInfo } from '../utils/financingCalculator';

const ClientQuoteView = () => {
  const { uniqueId } = useParams();
  const { user } = useAuth(); // Get current authenticated user
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTier, setSelectedTier] = useState('');
  const [signatureName, setSignatureName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const acceptFormRef = useRef(null);
  
  // Interactive Products State
  const [interactiveProducts, setInteractiveProducts] = useState([]);
  const [productQuantities, setProductQuantities] = useState({});
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [interactiveTotal, setInteractiveTotal] = useState(0);

  useEffect(() => {
    if (uniqueId) {
      loadQuote();
    }
  }, [uniqueId, user]);

  const loadQuote = async () => {
    try {
      console.log('🔥 QUOTE VIEW v4.5 - DIRECT FETCH BYPASS!');
      console.log('🔍 Loading quote for uniqueId:', uniqueId);
      
      const response = await quoteService.getQuoteByUniqueId(uniqueId);
      console.log('✅ Quote service response:', response);

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Quote not found');
      }

      const data = response.data;
      
      console.log('🔍 DETAILED DATA INSPECTION:');
      console.log('  - Quote ID:', data.id);
      console.log('  - User ID:', data.user_id || data.userId);
      console.log('  - Client Name:', data.clientName); // FIXED: Use transformed property
      console.log('  - Good Title:', data.tiers?.good?.title); // FIXED: Use transformed property
      console.log('  - Good Price:', data.tiers?.good?.price); // FIXED: Use transformed property
      console.log('  - Status:', data.status);
      console.log('  - Created At:', data.createdAt);
      console.log('  - Profile Data:', data.owner); // FIXED: Use transformed property
      
      console.log('📝 BULLET POINTS DEBUG:');
      console.log('  - Good bulletPoints:', data.tiers?.good?.bulletPoints);
      console.log('  - Better bulletPoints:', data.tiers?.better?.bulletPoints);
      console.log('  - Best bulletPoints:', data.tiers?.best?.bulletPoints);
      
      if (data.owner) {
        console.log('  - Business Name:', data.owner.businessName);
        console.log('  - Full Name:', data.owner.name);
        console.log('  - Avatar URL:', data.owner.avatarUrl);
      } else {
        console.warn('⚠️ NO PROFILE DATA - data.owner is null/undefined');
      }
      
      const hasRealData = data.clientName && data.tiers?.good?.title && data.tiers?.good?.price && (data.userId || data.user_id);
      console.log('📊 Real Data Check:', hasRealData ? '✅ HAS REAL DATA' : '❌ MISSING CORE DATA');
      console.log('🔍 Data check details:');
      console.log('  - clientName:', !!data.clientName, data.clientName);
      console.log('  - good title:', !!data.tiers?.good?.title, data.tiers?.good?.title);
      console.log('  - good price:', !!data.tiers?.good?.price, data.tiers?.good?.price);
      console.log('  - userId/user_id:', !!(data.userId || data.user_id), data.userId || data.user_id);
      console.log('  - interactiveEnabled:', data.interactiveEnabled);
      
      // FIXED: Data is already transformed by the service, just use it directly!
      console.log('🔄 Using already-transformed data:', data);
      
      // ALERT USER IF WE'RE SHOWING FALLBACK DATA
      if (!hasRealData) {
        console.error('🚨 DISPLAYING FALLBACK DATA - QUOTE NOT FOUND IN DATABASE!');
        console.error('💡 This usually means:');
        console.error('   1. Quote with this unique_id does not exist');
        console.error('   2. Database is empty');
        console.error('   3. RLS policies are blocking access');
      }
      
      setQuote(data); 
      
      // REMOVED: Auto pre-filling email to prevent wrong email addresses
      // Users must manually enter their email address for security
      
      try {
        // 🔍 TRACK QUOTE VIEW (with timeout protection)
        const trackingTimeout = setTimeout(() => {
          console.log('⚠️ Quote view tracking timed out - continuing anyway');
        }, 3000);
        
        const result = await Promise.race([
          quoteService.trackQuoteView(uniqueId, user?.id || null),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Tracking timeout')), 3000)
          )
        ]);
        
        clearTimeout(trackingTimeout);
        
        if (result.success) {
          // Check for the specific ownerView flag
          if (result.ownerView) {
            console.log('👤 Owner view detected. Tracking skipped as intended.');
          } else {
            console.log('✅ Client view tracked successfully.');
          }
        } else {
          console.log('⚠️ Failed to track quote view but continuing:', result.message);
        }
      } catch (trackingError) {
        console.log('⚠️ Quote tracking failed but continuing:', trackingError.message);
        // 🚑 TRACKING FAILURE IS NOT CRITICAL - Continue loading quote
      }
      
      const userId = data.userId || data.user_id;
      if (data.interactiveEnabled && userId) {
        console.log('🔍 Loading interactive products for user:', userId);
        await loadInteractiveProducts(userId);
      } else {
        console.log('⚠️ Interactive products NOT loading:');
        console.log('  - interactiveEnabled:', data.interactiveEnabled);
        console.log('  - userId available:', !!userId);
      }
      
      if (data.status === 'accepted') {
        setAccepted(true);
        setSelectedTier(data.selectedTier);
      }
      
    } catch (error) {
      console.error('❌ Error loading quote:', error);
      console.error('❌ Full error:', error.message, error.stack);
      
      if (error.message.includes('not found')) {
        toast.error('Quote not found or has expired');
      } else if (error.message.includes('expired')) {
        toast.error('This quote has expired');
      } else {
        toast.error(`Failed to load quote: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadInteractiveProducts = async (userId) => {
    if (!userId) {
      console.warn('⚠️ PRODUCTS: No userId provided, skipping product load');
      return;
    }
    
    setLoadingProducts(true);
    try {
      console.log('🔍 Loading interactive products with comprehensive error handling...');
      
      // ⚡ AGGRESSIVE TIMEOUT: 3 seconds max
      const productsTimeout = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Interactive products timeout')), 3000)
      );
      
      const result = await Promise.race([
        quoteService.getInteractiveProducts(userId),
        productsTimeout
      ]);
      
      // Handle both success and graceful failures
      if (result && result.success) {
        const products = result.data || [];
        console.log('✅ Interactive products loaded successfully:', products.length, 'products');
        setInteractiveProducts(products);
        
        // Initialize quantities for all products
        const initialQuantities = {};
        products.forEach(product => {
          initialQuantities[product.id] = 0;
        });
        setProductQuantities(initialQuantities);
        
        if (products.length === 0) {
          console.log('📄 No interactive products found - this is normal for new businesses');
        }
      } else {
        // Handle service failure gracefully
        console.warn('⚠️ Interactive products service returned error:', result?.error || 'Unknown error');
        console.log('🔄 Continuing with empty products list');
        setInteractiveProducts([]);
        setProductQuantities({});
      }
      
    } catch (error) {
      console.error('❌ Failed to load interactive products:', error.message);
      
      // Categorize different types of failures
      if (error.message.includes('timeout')) {
        console.warn('⏰ Interactive products timed out - likely database/network issue');
        toast.info('Interactive products are taking longer than expected to load');
      } else if (error.message.includes('permission') || error.message.includes('denied')) {
        console.warn('🔒 Interactive products access denied - likely RLS policy issue');
      } else if (error.message.includes('not exist')) {
        console.warn('📄 Interactive products table does not exist - feature not set up');
      } else {
        console.warn('❌ Unknown interactive products error:', error.message);
      }
      
      // Always set to empty arrays to prevent UI issues
      setInteractiveProducts([]);
      setProductQuantities({});
      
      // Don't show error toast for missing features (graceful degradation)
      if (!error.message.includes('not exist') && !error.message.includes('timeout')) {
        console.log('🐛 Unexpected products error - may need investigation');
      }
      
    } finally {
      setLoadingProducts(false);
      console.log('✅ Interactive products loading completed (success or graceful failure)');
    }
  };

  const updateProductQuantity = useCallback((productId, quantity) => {
    setProductQuantities(prev => {
      const updated = { ...prev, [productId]: Math.max(0, quantity) };
      
      // FIXED: Calculate total using quoteService.calculateOptimalPrice
      const total = Object.entries(updated).reduce((sum, [id, qty]) => {
        const product = interactiveProducts.find(p => p.id === id);
        if (product && qty > 0) {
          const priceCalc = quoteService.calculateOptimalPrice(product.pricing_tiers, qty, product.base_price);
          return sum + priceCalc.total_price;
        }
        return sum;
      }, 0);
      
      setInteractiveTotal(total);
      return updated;
    });
  }, [interactiveProducts]);

  const calculateOptimalPrice = (product, quantity) => {
    if (!product || quantity <= 0) return product?.base_price || 0;
    
    const priceCalc = quoteService.calculateOptimalPrice(product.pricing_tiers, quantity, product.base_price);
    return priceCalc.price_per_unit;
  };

  const calculateBulkDiscount = (product, quantity) => {
    if (!product || quantity <= 0) return 0;
    
    const priceCalc = quoteService.calculateOptimalPrice(product.pricing_tiers, quantity, product.base_price);
    return priceCalc.savings;
  };

  // FIXED: Added missing handleBullet function for interactive products
  const handleBullet = (productId, event) => {
    const newQuantity = parseInt(event.target.value) || 0;
    updateProductQuantity(productId, Math.max(0, newQuantity));
  };

  const handleTierSelect = (tierName) => {
    if (quote.status === 'accepted') return;
    setSelectedTier(tierName);
  };
  const scrollToAcceptForm = () => {
    if (acceptFormRef.current) {
      acceptFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleAcceptQuote = async (e) => {
    e.preventDefault();
    
    if (!selectedTier) {
      toast.error('Please select a package');
      return;
    }
    
    if (!signatureName.trim()) {
      toast.error('Please enter your name to sign');
      return;
    }
    
    if (!clientEmail.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    
    if (!agreed) {
      toast.error('Please agree to the terms');
      return;
    }

    setSubmitting(true);
    
    try {
      console.log('🎯 STARTING FULL QUOTE ACCEPTANCE WORKFLOW');
      
      // Step 1: Accept the quote in database
      const acceptanceData = {
        selectedTier,
        clientSignatureName: signatureName.trim(),
        clientEmail: clientEmail.trim(),
        interactiveSelections: productQuantities,
        interactiveTotal: interactiveTotal,
        signedAt: new Date().toISOString()
      };
      
      console.log('📝 Step 1: Accepting quote in database...');
      const { data: updatedQuote } = await quoteService.acceptQuote(uniqueId, acceptanceData);
      
      const totalValue = (quote.tiers[selectedTier]?.price || 0) + interactiveTotal;
      
      // Update local state
      setAccepted(true);
      setQuote(prev => ({
        ...prev,
        status: 'accepted',
        selectedTier: selectedTier,
        finalTotal: totalValue,
        clientEmail: clientEmail.trim(),
        clientSignatureName: signatureName.trim()
      }));
      
      // Step 2: Generate PDF
      console.log('📄 Step 2: Generating professional PDF...');
      
      const quoteForPDF = {
        ...quote,
        uniqueId,
        status: 'accepted',
        selectedTier,
        clientEmail: clientEmail.trim(),
        clientSignatureName: signatureName.trim(),
        interactiveTotal: interactiveTotal,
        finalTotal: totalValue
      };
      
      const pdfBlob = await quotePDFService.generatePDFBlob(quoteForPDF, acceptanceData);
      console.log('✅ PDF generated successfully:', pdfBlob.size, 'bytes');
      
      // Step 3: Send confirmation emails with PDF attachment
      console.log('📧 Step 3: Sending confirmation emails...');
      
      try {
        await quoteEmailService.sendQuoteAcceptanceEmails(quoteForPDF, acceptanceData, pdfBlob);
        console.log('✅ Confirmation emails sent successfully');
        
        toast.success(
          <div>
            <div className="font-bold">Quote Accepted Successfully!</div>
            <div className="text-sm">Confirmation emails sent with PDF attachment</div>
          </div>,
          { autoClose: 6000 }
        );
      } catch (emailError) {
        console.warn('⚠️ Email sending failed, but quote was accepted:', emailError);
        
        toast.success(
          <div>
            <div className="font-bold">Quote Accepted Successfully!</div>
            <div className="text-sm text-yellow-600">Note: Email confirmation failed to send</div>
          </div>,
          { autoClose: 6000 }
        );
      }
      
      // Step 4: Offer PDF download as backup
      try {
        await quotePDFService.downloadQuotePDF(quoteForPDF, acceptanceData, `quote-${uniqueId}-accepted.pdf`);
        console.log('✅ PDF download initiated');
      } catch (downloadError) {
        console.warn('⚠️ PDF download failed:', downloadError);
      }
      
      console.log('🎉 QUOTE ACCEPTANCE WORKFLOW COMPLETED SUCCESSFULLY');
      
    } catch (error) {
      console.error('❌ Quote acceptance workflow error:', error);
      
      // Check if quote was accepted but PDF/email failed
      if (error.message?.includes('PDF') || error.message?.includes('email')) {
        toast.error(
          <div>
            <div className="font-bold">Quote Accepted, but PDF/Email Failed</div>
            <div className="text-sm">Your quote was accepted successfully, but we couldn't generate the PDF or send emails. Please contact the business owner.</div>
          </div>,
          { autoClose: 8000 }
        );
      } else {
        toast.error(
          <div>
            <div className="font-bold">Failed to Accept Quote</div>
            <div className="text-sm">{error.message || 'Please try again or contact support'}</div>
          </div>,
          { autoClose: 6000 }
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // LANDING PAGE STYLE LOADING
  if (loading) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-black border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
          <h1 className="text-2xl font-bold text-foreground font-instrument mb-2">Loading Quote</h1>
          <p className="text-muted-foreground font-jetbrains">Please wait while we load your quote...</p>
        </div>
      </div>
    );
  }

  // LANDING PAGE STYLE ERROR
  if (!quote) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <h1 className="text-3xl font-bold text-foreground mb-4 font-instrument">Quote Not Found</h1>
          <p className="text-muted-foreground font-jetbrains mb-6">
            This quote may have expired or does not exist. Please check the URL or contact the business owner.
          </p>
          <div className="border border-black rounded-lg p-4 bg-white">
            <p className="text-sm text-muted-foreground font-jetbrains">
              Quote ID: {uniqueId}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // MAIN LANDING PAGE STYLE QUOTE VIEW
  return (
    <div className="min-h-screen bg-background text-foreground font-jakarta">
      {/* Mobile sticky summary/CTA */}
      {selectedTier && !accepted && (
        <div className="fixed bottom-0 inset-x-0 z-30 border-t border-gray-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 md:hidden">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <div>
              <div className="text-xs text-gray-600">Total</div>
              <div className="text-lg font-semibold text-gray-900">{formatPrice((quote.tiers[selectedTier]?.price || 0) + interactiveTotal)}</div>
            </div>
            <button onClick={scrollToAcceptForm} className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800">
              Accept Quote
            </button>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-8">
        
        {/* LANDING PAGE STYLE HEADER */}
        <div className="rounded-lg mb-8 bg-white shadow-sm ring-1 ring-gray-200 overflow-hidden">
          <div className="document-header px-6 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {quote.owner?.avatarUrl ? (
                  <img
                    src={quote.owner.avatarUrl}
                    alt="Business logo"
                    className="h-12 w-12 rounded-lg object-cover border border-white/20"
                  />
                ) : (
                  <div className="h-12 w-12 rounded-lg border border-white/20 bg-white/10 flex items-center justify-center">
                    <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                )}
                <div>
                  <h1 className="text-xl font-semibold text-slate-900">{quote.owner?.businessName}</h1>
                  <p className="text-slate-600">Quote for {quote.clientName}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-slate-500 text-xs">Quote ID</div>
                <div className="text-slate-900 font-semibold">#{uniqueId.substring(0, 8).toUpperCase()}</div>
                {quote.expiresAt && (
                  <div className="text-slate-500 text-xs mt-1">Valid until {formatDate(quote.expiresAt)}</div>
                )}
              </div>
            </div>
          </div>

          {/* ACCEPTED STATUS */}
          {accepted && (
            <div className="px-6 py-4 bg-green-50 border-b border-green-200">
              <div className="flex items-center">
                <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                  <svg className="h-4 w-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <div className="font-bold text-green-900 font-instrument">Quote Accepted!</div>
                  <div className="text-green-700 font-jetbrains text-sm">
                    {quote.tiers[selectedTier]?.title} - {formatPrice((quote.tiers[selectedTier]?.price || 0) + interactiveTotal)}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* LANDING PAGE STYLE PACKAGES */}
        <div className="rounded-lg mb-8 bg-white shadow-sm ring-1 ring-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-semibold text-slate-900">Choose Your Package</h2>
            <p className="text-slate-600 text-sm">Select the option that best fits your needs</p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {quote.tiers && Object.entries(quote.tiers).map(([tierName, tier], index) => {
                const isPopular = tierName === 'better';
                const isSelected = selectedTier === tierName;
                return (
                  <div
                    key={tierName}
                    onClick={() => handleTierSelect(tierName)}
                    className={`package-card relative rounded-lg cursor-pointer transition-all flex flex-col h-full min-h-[400px] md:min-h-[300px] p-4 md:p-6 ring-1 ${
                      isSelected 
                        ? 'ring-2 ring-emerald-500 bg-emerald-50/50' 
                        : 'ring-gray-200 hover:ring-slate-900/10 hover:shadow-sm bg-white'
                    } ${quote.status === 'accepted' ? 'cursor-not-allowed opacity-75' : ''}`}
                  >
                    {isPopular && (
                      <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 z-10">
                        <div className="bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-sm">
                          Most Popular
                        </div>
                      </div>
                    )}
                    
                    {/* CARD HEADER - Fixed height */}
                    <div className="text-center p-4 pb-2">
                      <h3 className="text-base font-semibold text-slate-900 mb-2 capitalize">{tierName}</h3>
                      <div className="text-2xl font-semibold text-slate-900 mb-2">
                        {formatPrice(tier.price)}
                        {/* HVAC P0: Monthly payment display */}
                        {quote.showMonthlyPayments && (
                          <div className="text-sm font-medium text-slate-600 cursor-pointer hover:text-slate-900" 
                               title={getMonthlyPaymentInfo(tier.price, {
                                 apr: quote.financingApr,
                                 termMonths: quote.financingTermMonths,
                                 downPayment: quote.financingDownPayment
                               }).termsText}>
                            From {getMonthlyPaymentInfo(tier.price, {
                              apr: quote.financingApr,
                              termMonths: quote.financingTermMonths,
                              downPayment: quote.financingDownPayment
                            }).formattedPayment}
                          </div>
                        )}
                      </div>
                      <h4 className="text-sm font-medium text-slate-700 mb-4">{tier.title}</h4>
                    </div>
                    
                    {/* CARD CONTENT - Flexible height */}
                    <div className="flex-1 px-4 pb-4">
                      <div className="text-left space-y-2 h-full flex flex-col">
                        {/* SHOW DESCRIPTION FIRST (if it exists) */}
                        {tier.description && (
                          <div className="text-sm text-slate-700 mb-3 p-3 bg-gray-50 rounded-md border border-gray-200">
                            {tier.description}
                          </div>
                        )}
                        
                        {/* THEN SHOW BULLET POINTS (if they exist) - Flexible space */}
                        <div className="flex-1">
                          {(tier.bulletPoints && tier.bulletPoints.length > 0) && (
                            <div className="space-y-2">
                              {tier.bulletPoints.map((feature, index) => (
                                <div key={index} className="text-sm text-slate-700 flex items-start">
                                  <div className="w-4 h-4 bg-emerald-600 rounded-full flex items-center justify-center mr-2 mt-0.5 flex-shrink-0">
                                    <svg className="w-2 h-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                    </svg>
                                  </div>
                                  <span className="flex-1">{feature.trim()}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* FALLBACK: Show message if neither exists */}
                          {!tier.description && (!tier.bulletPoints || tier.bulletPoints.length === 0) && (
                            <div className="text-sm text-slate-500 italic text-center py-4">
                              No details available for this package
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* CARD FOOTER - Always at bottom */}
                    <div className="p-4 pt-0 mt-auto">
                      {isSelected ? (
                        <div className="bg-slate-900 text-white py-3 px-4 rounded-lg font-semibold flex items-center justify-center w-full">
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Selected
                        </div>
                      ) : (
                        <div className="border border-gray-300 text-slate-700 py-3 px-4 rounded-lg font-medium hover:border-slate-900 hover:text-slate-900 transition-colors text-center w-full">
                          Select Package
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* HVAC P0: Equipment & Ratings Section */}
        {selectedTier && quote.equipmentMetadata && quote.equipmentMetadata[selectedTier] && (
          <div className="rounded-lg mb-8 bg-blue-50 shadow-sm ring-1 ring-blue-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-blue-200 bg-blue-100">
              <h2 className="text-lg font-semibold text-blue-900">Equipment & Ratings</h2>
              <p className="text-blue-700 text-sm">Technical specifications for your {selectedTier} package</p>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Equipment Models */}
                {quote.equipmentMetadata[selectedTier].modelOutdoor && (
                  <div>
                    <div className="text-sm font-semibold text-blue-900 mb-1">Outdoor Unit</div>
                    <div className="text-sm text-blue-800">{quote.equipmentMetadata[selectedTier].modelOutdoor}</div>
                  </div>
                )}
                {quote.equipmentMetadata[selectedTier].modelIndoor && (
                  <div>
                    <div className="text-sm font-semibold text-blue-900 mb-1">Indoor Unit/Coil</div>
                    <div className="text-sm text-blue-800">{quote.equipmentMetadata[selectedTier].modelIndoor}</div>
                  </div>
                )}
                {quote.equipmentMetadata[selectedTier].modelFurnace && (
                  <div>
                    <div className="text-sm font-semibold text-blue-900 mb-1">Furnace Model</div>
                    <div className="text-sm text-blue-800">{quote.equipmentMetadata[selectedTier].modelFurnace}</div>
                  </div>
                )}
                
                {/* Efficiency Ratings */}
                {quote.equipmentMetadata[selectedTier].seer2 && (
                  <div>
                    <div className="text-sm font-semibold text-blue-900 mb-1">SEER2 Rating</div>
                    <div className="text-sm text-blue-800">{quote.equipmentMetadata[selectedTier].seer2}</div>
                  </div>
                )}
                {quote.equipmentMetadata[selectedTier].hspf2 && (
                  <div>
                    <div className="text-sm font-semibold text-blue-900 mb-1">HSPF2 Rating</div>
                    <div className="text-sm text-blue-800">{quote.equipmentMetadata[selectedTier].hspf2}</div>
                  </div>
                )}
                {quote.equipmentMetadata[selectedTier].afue && (
                  <div>
                    <div className="text-sm font-semibold text-blue-900 mb-1">AFUE Rating</div>
                    <div className="text-sm text-blue-800">{quote.equipmentMetadata[selectedTier].afue}%</div>
                  </div>
                )}
                
                {/* AHRI Information */}
                {quote.equipmentMetadata[selectedTier].ahriReference && (
                  <div>
                    <div className="text-sm font-semibold text-blue-900 mb-1">AHRI Reference</div>
                    <div className="text-sm text-blue-800">{quote.equipmentMetadata[selectedTier].ahriReference}</div>
                  </div>
                )}
              </div>
              
              {/* AHRI Certificate Link */}
              {quote.equipmentMetadata[selectedTier].ahriCertificateUrl && (
                <div className="mt-4 pt-4 border-t border-blue-200">
                  <a 
                    href={quote.equipmentMetadata[selectedTier].ahriCertificateUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    View AHRI Certificate
                  </a>
                </div>
              )}
            </div>
          </div>
        )}

        {/* INTERACTIVE PRODUCTS - Gated until a package is selected */}
        {quote?.interactiveEnabled && interactiveProducts.length > 0 && selectedTier && (
          <div className="rounded-lg mb-8 bg-white shadow-sm ring-1 ring-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h2 className="text-lg font-semibold text-slate-900">Add-On Services</h2>
              <p className="text-slate-600 text-sm">Customize your package with additional services</p>
            </div>
            <div className="p-6">
              {loadingProducts ? (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-slate-600">Loading services...</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                  {interactiveProducts.map((product) => {
                    const quantity = productQuantities[product.id] || 0;
                    const currentPrice = calculateOptimalPrice(product, quantity);
                    const bulkSavings = calculateBulkDiscount(product, quantity);
                    const hasDiscount = bulkSavings > 0;
                    
                    return (
                      <div key={product.id} className="ring-1 ring-gray-200 rounded-lg p-4 hover:shadow-sm transition-all bg-white">
                        <div className="text-center mb-4">
                          <h3 className="text-base font-semibold text-slate-900 mb-2">{product.name}</h3>
                          <p className="text-sm text-slate-700 mb-3">{product.description}</p>
                          
                          <div className="text-center mb-4">
                            {hasDiscount ? (
                              <div>
                                <div className="text-sm line-through text-slate-400">{formatPrice(product.base_price)} per {product.unit_name}</div>
                                <div className="text-xl font-semibold text-slate-900">{formatPrice(currentPrice)} per {product.unit_name}</div>
                                <div className="text-xs text-emerald-700 font-medium">You’re saving {formatPrice(bulkSavings)} total</div>
                              </div>
                            ) : (
                              <div className="text-xl font-semibold text-slate-900">{formatPrice(currentPrice)} per {product.unit_name}</div>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-center space-x-4 mb-4">
                          <button
                            onClick={() => updateProductQuantity(product.id, quantity - 1)}
                            disabled={quantity <= 0}
                            className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed ring-1 ring-gray-300"
                          >
                            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                            </svg>
                          </button>
                          
                          <div className="text-center">
                            <div className="text-lg font-semibold text-slate-900">{quantity}</div>
                            <div className="text-xs text-slate-500">Qty ({product.unit_name})</div>
                          </div>
                          
                          <button
                            onClick={() => updateProductQuantity(product.id, quantity + 1)}
                            className="w-8 h-8 bg-slate-900 rounded-full flex items-center justify-center hover:bg-slate-800 text-white"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                          </button>
                        </div>
                        
                        {quantity > 0 && (
                          <div className="bg-gray-50 rounded-md p-3 text-center ring-1 ring-gray-200">
                            <div className="text-xs text-slate-600">Subtotal</div>
                            <div className="text-lg font-semibold text-slate-900">{formatPrice(currentPrice * quantity)}</div>
                            {hasDiscount && (
                              <div className="text-xs text-emerald-700 font-medium">
                                Total savings: {formatPrice(bulkSavings)}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              
              {interactiveTotal > 0 && (
                <div className="mt-6 p-4 bg-gray-50 rounded-md ring-1 ring-gray-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-base font-semibold text-slate-900">Add-ons Total</div>
                      <div className="text-sm text-slate-600">Additional services</div>
                    </div>
                    <div className="text-lg font-semibold text-slate-900">{formatPrice(interactiveTotal)}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* QUOTE SUMMARY - LANDING PAGE STYLE */}
        {selectedTier && (
          <div className="rounded-lg mb-8 bg-white shadow-sm ring-1 ring-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h2 className="text-lg font-semibold text-slate-900">Quote Summary</h2>
              <p className="text-slate-600 text-sm">Review your selection</p>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <div>
                    <div className="text-base font-semibold text-slate-900">{quote.tiers[selectedTier]?.title}</div>
                    <div className="text-sm text-slate-600">Base package</div>
                  </div>
                  <div className="text-lg font-semibold text-slate-900">{formatPrice(quote.tiers[selectedTier]?.price || 0)}</div>
                </div>
                
                {interactiveTotal > 0 && (
                  <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <div>
                      <div className="text-base font-semibold text-slate-900">Add-on Services</div>
                      <div className="text-sm text-slate-600">Additional products and services</div>
                    </div>
                    <div className="text-lg font-semibold text-slate-900">{formatPrice(interactiveTotal)}</div>
                  </div>
                )}

                {/* HVAC P0: Rebates section */}
                {quote.rebates && quote.rebates.length > 0 && (
                  <div className="py-3 border-b border-gray-200">
                    <div className="text-base font-semibold text-slate-900 mb-2">Estimated Rebates & Incentives</div>
                    {quote.rebates.map((rebate, index) => {
                      const baseAmount = (quote.tiers[selectedTier]?.price || 0) + interactiveTotal;
                      const rebateAmount = rebate.amount_type === 'percentage' 
                        ? baseAmount * (rebate.amount_value / 100)
                        : rebate.amount_value;
                      
                      return (
                        <div key={index} className="flex justify-between items-center py-1">
                          <div className="text-sm text-slate-600">
                            {rebate.label}
                            {rebate.notes && (
                              <div className="text-xs text-slate-500">{rebate.notes}</div>
                            )}
                          </div>
                          <div className="text-sm font-medium text-green-600">-{formatPrice(rebateAmount)}</div>
                        </div>
                      );
                    })}
                    <div className="text-xs text-slate-500 mt-2 italic">
                      *Estimates only. Rebates vary by ZIP/utility. Subject to qualification.
                    </div>
                  </div>
                )}
                
                <div className="bg-gray-50 rounded-md p-4 ring-1 ring-gray-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-lg font-semibold text-slate-900">Total Investment</div>
                      <div className="text-sm text-slate-600">Complete project cost</div>
                    </div>
                    <div className="text-2xl font-semibold text-slate-900">{formatPrice((quote.tiers[selectedTier]?.price || 0) + interactiveTotal)}</div>
                  </div>
                  {interactiveTotal > 0 && (
                    <div className="mt-3 text-sm text-slate-600">
                      <div className="flex justify-between">
                        <span>Base: {formatPrice(quote.tiers[selectedTier]?.price || 0)}</span>
                        <span>Add-ons: {formatPrice(interactiveTotal)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ELECTRONIC SIGNATURE - LANDING PAGE STYLE */}
        {selectedTier && !accepted && (
          <div ref={acceptFormRef} className="rounded-lg bg-white shadow-sm ring-1 ring-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h2 className="text-lg font-semibold text-slate-900">Accept Quote</h2>
              <p className="text-slate-600 text-sm">Complete your order below</p>
            </div>
            <div className="p-6">
              <form onSubmit={handleAcceptQuote} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="clientEmail" className="block text-sm md:text-base font-medium text-slate-900 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="clientEmail"
                      name="clientEmail"
                      value={clientEmail}
                      onChange={(e) => setClientEmail(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-slate-900 text-base min-h-[48px]"
                      placeholder="Enter your email address"
                      autoComplete="email"
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="signatureName" className="block text-sm md:text-base font-medium text-slate-900 mb-2">
                      Electronic Signature *
                    </label>
                    <input
                      type="text"
                      id="signatureName"
                      value={signatureName}
                      onChange={(e) => setSignatureName(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-slate-900 text-base min-h-[48px]"
                      placeholder="Type your full name"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Legally binding under ESIGN Act & UETA</p>
                  </div>
                </div>
                
                <div className="bg-gray-50 ring-1 ring-gray-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <input
                      id="agreement"
                      type="checkbox"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                      className="h-4 w-4 text-slate-900 focus:ring-slate-900 border border-gray-300 rounded mt-1"
                      required
                    />
                    <div className="ml-3">
                      <label htmlFor="agreement" className="text-sm text-slate-700">
                        I accept the terms of this quote. Read our{' '}
                        <a href="/terms" target="_blank" rel="noreferrer" className="text-slate-900 underline underline-offset-2">Terms of Service</a>.
                      </label>
                      <p className="text-slate-600 mt-1 text-sm">
                        By checking this box, I confirm that I have reviewed this quote and authorize {quote.owner?.businessName} to proceed with the work as described.
                      </p>
                    </div>
                  </div>
                </div>
                
                <button
                  type="submit"
                  disabled={submitting || !selectedTier || !signatureName.trim() || !clientEmail.trim() || !agreed}
                  className="w-full bg-slate-900 text-white py-4 px-6 rounded-lg text-base md:text-lg font-semibold hover:bg-slate-800 focus:outline-none focus:ring-4 focus:ring-slate-300 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all min-h-[56px]"
                >
                  {submitting ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Processing...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Accept Quote — {formatPrice((quote.tiers[selectedTier]?.price || 0) + interactiveTotal)}
                    </div>
                  )}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* FOOTER - LANDING PAGE STYLE */}
        <div className="mt-8 text-center">
          <div className="border border-gray-200 rounded-lg p-4 bg-white">
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-600 mb-1 font-jetbrains">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Quote from <span className="font-semibold">{quote.owner?.name}</span></span>
            </div>
            <div className="text-lg font-bold text-gray-800 font-instrument">{quote.owner?.businessName}</div>
            <div className="text-xs text-gray-500 mt-1 font-jetbrains">Professional Quote System</div>
            
            {/* Free tier branding - hidden when owner has purchased white-label unlock */}
            {!quote.owner?.whiteLabelUnlocked && (quote.owner?.subscription_status === 'trialing' || !quote.owner?.subscription_status || quote.owner?.subscription_status === 'free') ? (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="flex items-center justify-center space-x-2 text-xs text-gray-500 font-jetbrains">
                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <span>Powered by <a href="https://quotetier.com" target="_blank" rel="noopener noreferrer" className="font-semibold text-gray-700 hover:text-gray-900 transition-colors">QuoteTier</a></span>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientQuoteView;
